import type { ReactElement } from 'react'
import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  InlineStack,
  Modal,
  Spinner,
  Text,
} from '@shopify/polaris'
import type { InvoiceWhatsNewEntryVM } from './contentful'
import { formatWhatsNewModalDate } from './date'
import { INVOICE_WHATSNEW_LIST_MAX_ITEMS } from './constants'
import { FordeerApp, getWhatsNewChangelogUrl } from '@fordeer/ui-components'

type InvoiceWhatsNewListModalProps = {
  open: boolean
  onClose: () => void
  entries: InvoiceWhatsNewEntryVM[]
  loading: boolean
  loadError: boolean
  onLearnMore: (entry: InvoiceWhatsNewEntryVM) => void
}

export const InvoiceWhatsNewListModal = ({
  open,
  onClose,
  entries,
  loading,
  loadError,
  onLearnMore,
}: InvoiceWhatsNewListModalProps): ReactElement => {
  const previewEntries = entries.slice(0, INVOICE_WHATSNEW_LIST_MAX_ITEMS)
  const changelogUrl = getWhatsNewChangelogUrl(FordeerApp.Invoice)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="What's New"
      primaryAction={
        changelogUrl
          ? {
              content: 'View Full Changelog',
              onAction: () => {
                window.open(changelogUrl, '_blank', 'noopener,noreferrer')
              },
            }
          : undefined
      }
      secondaryActions={[{ content: 'Close', onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {loading ? (
            <InlineStack align="center" blockAlign="center" gap="200">
              <Spinner size="small" />
              <Text as="span" variant="bodyMd">
                Loading updates…
              </Text>
            </InlineStack>
          ) : null}

          {!loading && loadError ? (
            <Text as="p" variant="bodyMd" tone="critical">
              We couldn&apos;t load updates. Please try again later.
            </Text>
          ) : null}

          {!loading && !loadError && !previewEntries.length ? (
            <Text as="p" variant="bodyMd" tone="subdued">
              There are no published updates for Invoice yet.
            </Text>
          ) : null}

          {!loading && !loadError
            ? previewEntries.map(entry => (
                <Card key={entry.id}>
                  <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center" gap="200">
                      <Badge tone={entry.label === 'New' ? 'attention' : 'success'}>
                        {entry.label}
                      </Badge>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {formatWhatsNewModalDate(entry.date ?? entry.createdAt)}
                      </Text>
                    </InlineStack>
                    <Text as="h3" variant="headingMd">
                      {entry.teaserTitle}
                    </Text>
                    {entry.teaserShortDescription ? (
                      <Text as="p" variant="bodyMd" tone="subdued">
                        {entry.teaserShortDescription}
                      </Text>
                    ) : null}
                    <Box>
                      <Button
                        variant="primary"
                        onClick={() => {
                          onLearnMore(entry)
                        }}
                      >
                        Learn more
                      </Button>
                    </Box>
                  </BlockStack>
                </Card>
              ))
            : null}
        </BlockStack>
      </Modal.Section>
    </Modal>
  )
}
